// ProteoformAbundances.tsx
import { DetailSection } from '@/components/DetailPage';
import { sampleQueryKeys } from '@/config/queryKeys';
import { useApiClient } from '@/hooks/useApiClient';
import type { PaginatedSampleProteoformsResponse, SampleProteoformPublic, SampleWithNamesPublic } from '@/types';
import { flushColumnState, sizeColumnsOnFirstVisit } from '@/utils/flushColumnState';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-enterprise';
import { Download } from 'lucide-react';
import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PROTEOFORM_SEARCH_BODY } from './constants';
import styles from './ProteoformAbundances.module.css';
import { collectPresentTokens, orderMatrixTokens, parseProteoformName } from './proteoformMatrix';
import { PROTEOFORM_TOKENS } from './proteoformTokens';

export interface ProteoformAbundancesProps {
  sample: SampleWithNamesPublic;
  orgId: string;
}

const THRESHOLD_STUB = 1000;
const tableId = (sampleId: string) => `proteoforms-matrix-${sampleId}`;

function stat(label: string, value: React.ReactNode) {
  return (
    <Stack className={styles.stat} key={label}>
      <Text as="span" className={styles.statLabel} size="xs">
        {label}
      </Text>
      <Text as="span" size="sm">
        {value}
      </Text>
    </Stack>
  );
}

function ProteoformAbundancesComponent({ sample, orgId }: ProteoformAbundancesProps) {
  const { t } = useTranslation('studies');
  // Proteoform matrix column-header keys (sampleDetails.col_*) live in the `runs` namespace.
  const { t: tCol } = useTranslation('runs');
  const api = useApiClient();
  const headerByToken = useMemo(() => new Map(PROTEOFORM_TOKENS.map(c => [c.token, c.i18nKey])), []);

  const { data } = useQuery({
    queryKey: sampleQueryKeys.proteoformsSearch(orgId, sample.id),
    queryFn: async () => {
      const url = sampleQueryKeys.endpoints.proteoformsSearch(orgId, sample.id);
      const response = await api.post<PaginatedSampleProteoformsResponse>(url, PROTEOFORM_SEARCH_BODY);
      if (response === null || response === undefined) throw new Error('No response from server');
      return response;
    },
    enabled: orgId !== '' && sample.id !== '',
    retry: 1,
  });

  const records = useMemo(() => data?.records ?? [], [data]);

  const columnDefs = useMemo<ColDef<SampleProteoformPublic>[]>(() => {
    const present = collectPresentTokens(records.map(r => r.proteoform_name));
    const tokens = orderMatrixTokens(present);
    const yes = t('yes', { ns: 'general' });
    const no = t('no', { ns: 'general' });

    // Parse each distinct proteoform_name once; matrix cells (rows × tokens)
    // otherwise re-split the same composite string on every render/scroll.
    const tokensByName = new Map<string, Set<string>>();
    const tokensFor = (name: string): Set<string> => {
      let parsed = tokensByName.get(name);
      if (parsed === undefined) {
        parsed = parseProteoformName(name);
        tokensByName.set(name, parsed);
      }
      return parsed;
    };

    const matrixCols: ColDef<SampleProteoformPublic>[] = tokens.map(token => {
      const i18nKey = headerByToken.get(token);
      return {
        colId: token,
        headerName: i18nKey !== undefined ? tCol(i18nKey as 'sampleDetails.col_n_terminus') : token,
        sortable: false,
        filter: false,
        valueGetter: ({ data: row }) => (row !== undefined && tokensFor(row.proteoform_name).has(token) ? yes : no),
      };
    });

    return [
      { headerName: tCol('sampleDetails.col_proteoform_group'), field: 'proteoform_name', flex: 1, sortable: true },
      {
        headerName: tCol('sampleDetails.col_abundance'),
        field: 'proteoform_abundance',
        sortable: true,
        valueFormatter: ({ value }: { value: number | null | undefined }) =>
          value !== null && value !== undefined ? value.toLocaleString() : '',
      },
      ...matrixCols,
    ];
  }, [records, t, headerByToken, tCol]);

  const toolbar = (
    <Stack className={styles.toolbar}>
      {/* TODO(backend): no per-sample export endpoint (only run-scoped download exists). */}
      <Tooltip
        content={t('samples.details.actionComingSoon')}
        placement="bottom"
        trigger={
          <Button data-testid="download-proteoforms-btn" isDisabled size="md" visual="unstyled">
            <Download size={20} />
            <Text as="span" noWrap size="md">
              {t('samples.details.download')}
            </Text>
          </Button>
        }
      />
      {stat(t('samples.details.statProteoforms'), sample.num_proteoform_groups)}
      {stat(t('samples.details.statPtms'), sample.num_ptms)}
      {stat(t('samples.details.statIsoforms'), sample.num_isoforms)}
      {/* TODO(backend): no threshold field/endpoint — static stub, pencil disabled. */}
      {stat(t('samples.details.threshold'), t('samples.details.thresholdValue', { value: THRESHOLD_STUB }))}
    </Stack>
  );

  return (
    <DetailSection
      data-testid="proteoform-abundances"
      title={t('samples.details.proteoformAbundancesHeading')}
      toolbar={toolbar}
    >
      <Table<SampleProteoformPublic>
        ariaLabel={t('samples.details.matrixAriaLabel')}
        columnDefs={columnDefs}
        density="compact"
        domLayout="autoHeight"
        enableResponsiveResize={false}
        pagination={data !== null && data !== undefined && data.total > data.limit}
        paginationPageSize={data?.limit}
        rowData={records}
        tableId={tableId(sample.id)}
        onGridReady={e => sizeColumnsOnFirstVisit(e.api, tableId(sample.id))}
        onGridPreDestroyed={e => flushColumnState(e.api, tableId(sample.id))}
        zebraStriping
      />
    </DetailSection>
  );
}

export const ProteoformAbundances = memo(ProteoformAbundancesComponent);
